













import Vue from 'vue';
import PSelectionChild from './p-selection-child.vue';

type ChildComponent = InstanceType<typeof PSelectionChild>;

export default Vue.extend({
  props: {
    value: { type: undefined, default: undefined },
    mandatory: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    max: { type: Number, default: undefined },
    activeClass: { type: String, default: 'p-selection-active' },
    children: { type: Array, default: () => [] },
    tag: { type: String, default: undefined },
  },
  data: () => ({
    activeChildren: [] as Array<ChildComponent>,
    allChildren: [] as Array<ChildComponent>,
  }),
  watch: {
    allChildren() {
      this.$emit('update:children', [...this.allChildren]);
    },
    value: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          // Avoid multiple updates when changing value internally
          this.$withoutWatchers(() => {
            this.populateActiveChildren();
          });
        });
      },
    },
  },
  methods: {
    populateActiveChildren() {
      this.activeChildren = [];
      if (Array.isArray(this.value)) {
        const arrayValue = ((this.value as any) as Array<any>);
        const activeChildren = this.allChildren.filter((i) => arrayValue.includes(i.value ?? this.allChildren.indexOf(i)));
        this.activeChildren = [...activeChildren];
        this.allChildren.forEach((i) => {
          i.isActive = this.activeChildren.includes(i);
        });
      } else {
        const child = this.allChildren.find((i) => (i.value ?? this.allChildren.indexOf(i)) === this.value);
        if (child !== undefined) {
          this.activeChildren = [];
          this.activeChildren.push(child);
          this.allChildren.forEach((i) => {
            i.isActive = i === child;
          });
        }
      }
    },
    updateValue() {
      if (this.activeChildren.length === 0) {
        this.$emit('input', undefined);
        this.$emit('component-selection', undefined);
        return;
      }
      const values = this.activeChildren.map((child) => child.value ?? this.allChildren.indexOf(child));
      if (this.multiple) {
        this.$emit('input', values);
        this.$emit('component-selection', this.activeChildren);
      } else {
        this.$emit('input', values[0]);
        this.$emit('component-selection', this.activeChildren[0]);
      }
    },

    deactivateChild(child: ChildComponent, doUpdateValue: boolean = true) {
      const index = this.activeChildren.indexOf(child);
      if (index >= 0) {
        this.activeChildren.splice(index, 1);
      }
      if (child.isActive) {
        child.isActive = false;
      }
      if (doUpdateValue) {
        this.updateValue();
      }
    },

    activateChild(child: ChildComponent, doUpdateValue: boolean = true) {
      if (!this.activeChildren.includes(child)) {
        this.activeChildren.push(child);
      }
      if (!child.isActive) {
        child.isActive = true;
      }
      if (doUpdateValue) {
        this.updateValue();
      }
    },

    toggleChild(child: ChildComponent) {
      const isActive = this.activeChildren.includes(child);
      if (!this.multiple) {
        this.activeChildren.forEach((i) => {
          this.deactivateChild(i, false);
        });
      }

      const isNotDeselection = !isActive;
      const needsMandatorySelection = this.mandatory && this.activeChildren.length <= 1;
      const isSelection = isNotDeselection || needsMandatorySelection;
      const isUnderMax = this.max > this.activeChildren.length || this.max === null || this.max === undefined;
      if (isSelection && isUnderMax) {
        this.activateChild(child);
      } else {
        this.deactivateChild(child);
      }
    },

    onRegister(child: ChildComponent) {
      child.activeClass = this.activeClass;
      if (this.mandatory && this.activeChildren.length === 0) {
        this.activateChild(child);
      }
      if (!this.multiple) {
        this.activeChildren.forEach((i) => {
          this.deactivateChild(i, false);
        });
      }
      this.allChildren.forEach((i) => {
        if (this.activeChildren.includes(i)) {
          this.activateChild(i);
        }
      });
      this.populateActiveChildren();
      child.$on('change', this.toggleChild);
      this.$emit('register', child);
    },

    onUnregister(child: ChildComponent) {
      this.deactivateChild(child);
      child.$off('change', this.toggleChild);
      this.allChildren.forEach((i) => {
        i.isActive = this.activeChildren.includes(i);
      });
      this.$emit('unregister', child);
    },
  },
});

